import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AdminAuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminAuthProvider = ({ children }) => {
    const [admin, setAdmin] = useState(null);
    const [token, setToken] = useState(() => localStorage.getItem('aeroops_admin_token'));
    const [loading, setLoading] = useState(true);

    const clearAuth = useCallback(() => {
        localStorage.removeItem('aeroops_admin_token');
        setToken(null);
        setAdmin(null);
    }, []);

    const fetchAdmin = useCallback(async (tkn) => {
        try {
            const response = await axios.get(`${API}/admin/auth/me`, {
                headers: { Authorization: `Bearer ${tkn}` }
            });
            setAdmin(response.data);
        } catch {
            clearAuth();
        } finally {
            setLoading(false);
        }
    }, [clearAuth]);

    useEffect(() => {
        if (token) {
            fetchAdmin(token);
        } else {
            setLoading(false);
        }
    }, [token, fetchAdmin]);

    const login = async (email, password, totpCode = null) => {
        const payload = { email, password };
        if (totpCode) payload.totp_code = totpCode;
        const response = await axios.post(`${API}/admin/auth/login`, payload);
        const { access_token, requires_2fa, requires_2fa_setup, user } = response.data;
        // Auditoria 8 - Item 1: superadmin sin 2FA configurado -> forzar setup en primer login
        if (requires_2fa_setup) {
            // Guardar token temporal para llamar setup-2fa + enable-2fa
            localStorage.setItem('aeroops_admin_setup_token', access_token);
            return { requires_2fa_setup: true, user };
        }
        if (requires_2fa) {
            return { requires_2fa: true };
        }
        if (response.data.requires_password_change) {
            localStorage.setItem('aeroops_admin_pwchange_token', access_token);
            return { requires_password_change: true };
        }
        localStorage.setItem('aeroops_admin_token', access_token);
        setToken(access_token);
        setAdmin(user);
        return { requires_2fa: false, user };
    };

    const complete2FASetup = async (code) => {
        const setupToken = localStorage.getItem('aeroops_admin_setup_token');
        if (!setupToken) throw new Error('No hay token de setup activo');
        const res = await axios.post(`${API}/admin/auth/enable-2fa`, { code }, {
            headers: { Authorization: `Bearer ${setupToken}` }
        });
        const newToken = res.data.access_token;
        localStorage.removeItem('aeroops_admin_setup_token');
        if (res.data.requires_password_change) {
            localStorage.setItem('aeroops_admin_pwchange_token', newToken);
            return { requires_password_change: true };
        }
        localStorage.setItem('aeroops_admin_token', newToken);
        setToken(newToken);
        await fetchAdmin(newToken);
        return true;
    };

    const fetchSetup2FA = async () => {
        const setupToken = localStorage.getItem('aeroops_admin_setup_token');
        if (!setupToken) throw new Error('No hay token de setup activo');
        const res = await axios.post(`${API}/admin/auth/setup-2fa`, {}, {
            headers: { Authorization: `Bearer ${setupToken}` }
        });
        return res.data; // {secret, uri}
    };

    const changeInitialPassword = async (currentPassword, newPassword) => {
        const pwToken = localStorage.getItem('aeroops_admin_pwchange_token');
        if (!pwToken) throw new Error('No hay cambio de contraseña pendiente');
        const res = await axios.post(`${API}/admin/auth/change-password`,
            { current_password: currentPassword, new_password: newPassword },
            { headers: { Authorization: `Bearer ${pwToken}` } });
        localStorage.removeItem('aeroops_admin_pwchange_token');
        localStorage.setItem('aeroops_admin_token', res.data.access_token);
        setToken(res.data.access_token);
        await fetchAdmin(res.data.access_token);
    };

    const logout = () => clearAuth();

    const apiCall = useCallback(async (method, url, data) => {
        return axios({ method, url: `${API}${url}`, data, headers: { Authorization: `Bearer ${token}` } });
    }, [token]);

    return (
        <AdminAuthContext.Provider value={{ admin, token, loading, login, logout, apiCall, complete2FASetup, fetchSetup2FA, changeInitialPassword, isAuthenticated: !!admin }}>
            {children}
        </AdminAuthContext.Provider>
    );
};

export const useAdminAuth = () => {
    const context = useContext(AdminAuthContext);
    if (!context) throw new Error('useAdminAuth must be used within AdminAuthProvider');
    return context;
};
